import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SOLICITUD_ALTERNATIVAS_API } from '../../../shared/services/solicitudAlternativas';
import ComentariosHistorialList from '../ComentariosHistorialList';
import InputSearch from '../InputSearch';
import ActionButton from '../ActionButton';
import LoadingInline from '../layout/LoadingInline';
import { ModalSection, ModalPanel } from '../layout/ModalSection';
import EnviarRespuestaAviso from '../layout/EnviarRespuestaAviso';
import PresupuestoOrigenFields from '../PresupuestoOrigenFields';
import AlternativaDetalleAlternativasGrid from '../AlternativaDetalleAlternativasGrid';
import { normalizePiezas } from '../../utils';

const ResponderSolicitudModal = ({
    currentItem,
    onClose,
    onSolicitudUpdated,
}) => {
    const [responderData, setResponderData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const id = currentItem?.id;
        if (id == null) {
            setResponderData(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await SOLICITUD_ALTERNATIVAS_API.getResponderData(
                    id,
                );
                if (cancelled) return;
                if (res?.success) {
                    setResponderData(res.data ?? null);
                } else {
                    toast.error('No se han podido cargar los datos.');
                    setResponderData(null);
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(
                        e?.response?.data?.message ||
                            e?.message ||
                            'Error al cargar datos.',
                    );
                    setResponderData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentItem?.id]);

    const [searchAlt, setSearchAlt] = useState('');
    const [searchingAlt, setSearchingAlt] = useState(false);
    const [alternativa, setAlternativa] = useState(null);
    const [confirmPrice, setConfirmPrice] = useState(false);
    const [observaciones, setObservaciones] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const piezasOrigen = normalizePiezas(responderData);
    const piezasOrigenCount =
        piezasOrigen.length || normalizePiezas(currentItem).length;

    const handleCopyLink = useCallback((url) => {
        if (!url) return;
        navigator.clipboard.writeText(url);
        toast.success('Link copiado al portapapeles.');
    }, []);

    const handleSearchAlternativa = async () => {
        if (!searchAlt || searchAlt.trim().length < 2) return;
        setSearchingAlt(true);
        setAlternativa(null);
        try {
            const res = await SOLICITUD_ALTERNATIVAS_API.searchAlternativa(
                searchAlt.trim(),
            );
            if (res?.success) {
                const data = res.data;
                const altPiezasCount = data?.piezas?.length ?? 0;
                if (
                    piezasOrigenCount > 0 &&
                    altPiezasCount !== piezasOrigenCount
                ) {
                    toast.warn(
                        altPiezasCount > piezasOrigenCount
                            ? `El presupuesto alternativa tiene ${altPiezasCount} pieza(s); la solicitud tiene ${piezasOrigenCount}.`
                            : `El presupuesto alternativa tiene ${altPiezasCount} pieza(s); la solicitud requiere ${piezasOrigenCount}.`,
                    );
                    return;
                }
                setAlternativa(data);
            } else {
                toast.error(
                    'No se ha encontrado ningún presupuesto con ese número.',
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Error al buscar presupuesto.',
            );
        } finally {
            setSearchingAlt(false);
        }
    };

    const handleSubmit = async () => {
        if (!alternativa) {
            toast.error(
                'Debes buscar y seleccionar un presupuesto alternativa.',
            );
            return;
        }

        const altPiezasCount = alternativa?.piezas?.length ?? 0;
        if (
            piezasOrigenCount > 0 &&
            altPiezasCount !== piezasOrigenCount
        ) {
            toast.error(
                `El presupuesto alternativa debe tener exactamente ${piezasOrigenCount} pieza(s), igual que la solicitud.`,
            );
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                solicitud_id: currentItem.id,
                presupuesto_alternativa_id: alternativa.id,
                confirm_price: confirmPrice,
                observaciones: observaciones.trim() || null,
            };
            const res = await SOLICITUD_ALTERNATIVAS_API.responder(payload);
            if (res?.success) {
                toast.success('Respuesta enviada correctamente.');
                onSolicitudUpdated?.(res.data);
                onClose();
            } else {
                toast.error(
                    res?.message || 'No se ha podido enviar la respuesta.',
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Error al enviar la respuesta.',
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <LoadingInline />;
    }

    return (
        <div>
            <ModalSection title="Presupuesto origen">
                <ModalPanel className="card p-3 p-md-4 mb-4">
                    <div className="row g-3">
                        <PresupuestoOrigenFields
                            layout="responder"
                            origen={responderData?.origen}
                            piezas={responderData?.piezas}
                            fechaLabel="Fecha presupuesto"
                            clienteDisplay="stack"
                        />
                    </div>
                </ModalPanel>
            </ModalSection>

            {responderData?.comentarios_historial?.length > 0 ? (
                <ModalSection title="Comentario de la solicitud (pendiente)">
                    <ModalPanel className="card p-3 p-md-4 mb-4">
                        <ComentariosHistorialList
                            comentarios={responderData.comentarios_historial}
                        />
                    </ModalPanel>
                </ModalSection>
            ) : null}

            <ModalSection title="Presupuesto alternativa">
                <ModalPanel className="card p-3 p-md-4 mb-4">
                    <InputSearch
                        value={searchAlt}
                        setSearch={setSearchAlt}
                        handleSearch={handleSearchAlternativa}
                        isLoading={searchingAlt}
                    />
                    <AlternativaDetalleAlternativasGrid
                        variant="recambistaBusqueda"
                        alternativa={alternativa}
                        confirmPrice={confirmPrice}
                        onConfirmPriceChange={setConfirmPrice}
                        onCopyPiezaUrl={handleCopyLink}
                    />
                </ModalPanel>
            </ModalSection>

            <ModalSection title="Observaciones del recambista">
                <textarea
                    className="form-control mb-4"
                    rows={4}
                    placeholder="Escribe aquí cualquier detalle importante que Atención al Cliente tenga que saber sobre la alternativa..."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                />
            </ModalSection>

            <EnviarRespuestaAviso />

            <div className="d-flex justify-content-end">
                <ActionButton
                    disabled={submitting || !alternativa}
                    loading={submitting}
                    label="Enviar respuesta"
                    onClick={handleSubmit}
                />
            </div>
        </div>
    );
};

export default ResponderSolicitudModal;
